import { toast } from "sonner";

/**
 * Triggers an Electron auto-updater check and surfaces the result via
 * sonner toasts.
 *
 * This is the body of the `app.checkForUpdates` command. The Electron
 * preload exposes `window.__electronApi.updater` with `checkNow()` and
 * `onEvent()`; this helper subscribes once, races them against a 30s
 * timeout, and emits a single terminal toast describing the outcome.
 *
 * Web build has no `__electronApi` — emits a "desktop-only" message and
 * returns. Failure modes (timeout, error event, rejected `checkNow`)
 * each finalize the same toast id so we never end up with two competing
 * toasts on screen.
 */
export type UpdaterState =
  | { kind: "idle" | "checking" }
  | { kind: "not-available" }
  | { kind: "available"; availableVersion: string }
  | { kind: "downloading"; availableVersion: string }
  | { kind: "downloaded"; availableVersion: string }
  | { kind: "error"; message: string };

export interface UpdaterApi {
  checkNow(): Promise<void>;
  onEvent(cb: (state: UpdaterState) => void): () => void;
}

export function runUpdateCheck() {
  const updater = (
    window as unknown as { __electronApi?: { updater?: UpdaterApi } }
  ).__electronApi?.updater;
  if (!updater?.checkNow || !updater.onEvent) {
    toast.message("Updates are only available in the desktop app.");
    return;
  }

  const TOAST_ID = "update-check";
  toast.loading("Checking for updates…", { id: TOAST_ID });

  let settled = false;
  const finish = (fn: () => void) => {
    if (settled) return;
    settled = true;
    fn();
    unsubscribe();
    clearTimeout(timer);
  };
  const unsubscribe = updater.onEvent((state) => {
    if (state.kind === "not-available") {
      finish(() =>
        toast.success("You're on the latest version.", {
          id: TOAST_ID,
          duration: 4000,
        }),
      );
    } else if (state.kind === "available" || state.kind === "downloading") {
      finish(() =>
        toast.message(`Update available: v${state.availableVersion}`, {
          id: TOAST_ID,
          duration: 5000,
        }),
      );
    } else if (state.kind === "downloaded") {
      finish(() =>
        toast.success(`Update v${state.availableVersion} ready to install.`, {
          id: TOAST_ID,
          duration: 5000,
        }),
      );
    } else if (state.kind === "error") {
      finish(() =>
        toast.error(`Couldn't check for updates: ${state.message}`, {
          id: TOAST_ID,
          duration: 5000,
        }),
      );
    }
  });
  const timer = window.setTimeout(() => {
    finish(() =>
      toast.error("Update check timed out.", {
        id: TOAST_ID,
        duration: 4000,
      }),
    );
  }, 30_000);

  updater.checkNow().catch(() => {
    finish(() =>
      toast.error("Couldn't check for updates.", {
        id: TOAST_ID,
        duration: 4000,
      }),
    );
  });
}
